import {Injectable} from '@angular/core';
import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';

/**
 * kobe-ac 業務画面 (issue #160) の共有マスタキャッシュ。
 * 旧 kobe-ac.app では SignIn/Nav の load() が localStorage ($scope.master) に
 * 読み込んでいたものを、セッション内メモリキャッシュとして提供する。
 */
@Injectable({providedIn: 'root'})
export class KobeacService {
    /** マスター管理のメニュー名 → OrientDB クラス (旧 Database.findClassName の alias 対応表) */
    static readonly CLASS_BY_SHORTNAME: { [name: string]: string } = {
        '顧客': 'AC_Customer',
        'オークション': 'AC_Auction',
        'カテゴリ': 'AC_Category',
        '商品': 'AC_Product',
        'アイテム': 'AC_Item',
        'レポート': 'AC_Report',
        'マスタ': 'AC_Master',
        '設定': 'AC_Configuration',
    };

    auctions: any[] = [];
    customers: any[] = [];
    categories: any[] = [];
    configurations: any[] = [];

    private loading: Promise<void> | null = null;

    constructor(private dataService: DataService) {
    }

    /** マスタ 4 種を 1 回だけ読み込む。何度呼んでも同じ Promise を返す */
    ensureLoaded(): Promise<void> {
        if (!this.loading) {
            this.loading = this.load().catch((e) => {
                this.loading = null;
                throw e;
            });
        }
        return this.loading;
    }

    reload(): Promise<void> {
        this.loading = null;
        return this.ensureLoaded();
    }

    private queryAll(sql: string): Promise<any[]> {
        return new Promise((resolve, reject) => {
            this.dataService.query({sql: sql, count: sql, fetchPlan: '*:1'}, ActivePromotionConfig.SERVER_URL)
                .subscribe({
                    next: (data: any) => resolve(wjcCore.isArray(data.results) ? data.results : []),
                    error: (e: any) => reject(e),
                });
        });
    }

    private async load(): Promise<void> {
        [this.auctions, this.customers, this.categories, this.configurations] = await Promise.all([
            this.queryAll('SELECT FROM AC_Auction ORDER BY `日付` DESC LIMIT 5000'),
            this.queryAll('SELECT FROM AC_Customer ORDER BY ID ASC LIMIT 5000'),
            this.queryAll('SELECT FROM AC_Category ORDER BY `ソート` LIMIT 5000'),
            this.queryAll('SELECT FROM AC_Configuration WHERE ID = 1'),
        ]);
    }

    /** OrientDB の日付文字列を yyyy/MM/dd 表示にする (旧 orientdate フィルタ相当) */
    formatOrientDate(value: any): string {
        if (value == null) return '';
        if (wjcCore.isDate(value)) return wjcCore.Globalize.formatDate(value, 'yyyy/MM/dd');
        const d = new Date(String(value).replace(' ', 'T'));
        return isNaN(d.getTime()) ? String(value) : wjcCore.Globalize.formatDate(d, 'yyyy/MM/dd');
    }

    /**
     * link オブジェクト列用 DataMap。旧アプリ同様、@rid の一致でも引けるよう
     * _indexOf を差し替える (値が別インスタンスの同一レコードでも解決する)。
     */
    buildDataMap(items: any[], keyLabel: (item: any) => string): wjcGrid.DataMap {
        const rows = items.map((item) => ({key: item, text: keyLabel(item)}));
        const map = new wjcGrid.DataMap(rows, 'key', 'text');
        // key は LINK 先オブジェクト。Wijmo 既定の getDisplayValue は key を文字列化して
        // 内部マップを引くため、オブジェクトキーは全て "[object Object]" に衝突し、常に
        // 先頭要素の表示が返ってしまう (全行が同じ日付/名称になる不具合)。
        // value(セル値)は解決済みオブジェクトのことも @rid 文字列のこともあるため、
        // どちらでも @rid で線形照合する getDisplayValue / _indexOf に差し替える。
        const ridOf = (v: any) => (v != null && typeof v === 'object') ? v['@rid'] : v;
        (map as any).getDisplayValue = function (value: any) {
            const rid = ridOf(value);
            if (rid == null) return '';
            for (const r of rows) {
                if (String(ridOf(r.key)) === String(rid)) return r.text;
            }
            return value;
        };
        (map as any)._indexOf = function (value: any, path: string, caseSensitive: boolean) {
            const rid = ridOf(value);
            if (rid != null) {
                for (let i = 0; i < rows.length; i++) {
                    if (String(ridOf(rows[i].key)) === String(rid)) return i;
                }
            }
            // 表示文字列(text)からの逆引き (編集/フィルタ用)
            if (!caseSensitive && value != null) {
                const lcval = value.toString().toLowerCase();
                for (let i = 0; i < rows.length; i++) {
                    if (rows[i].text != null && rows[i].text.toLowerCase() === lcval) return i;
                }
            }
            return -1;
        };
        return map;
    }

    /**
     * AC_Item の 買主/売主/オークション が @rid 文字列で返った場合にマスタの
     * オブジェクトへ解決する。DataMap(getDisplayValue) は key(オブジェクト)を
     * 参照一致で引くため、@rid 文字列のままだと変換できず @rid が表示される。
     */
    resolveCustomers(items: any[]): any[] {
        for (const item of items) {
            if (wjcCore.asString(item['@class']) == 'AC_Item') {
                if (wjcCore.isString(item['買主'])) {
                    const c = this.customers.filter((x) => x['@rid'] == item['買主']);
                    if (c.length) item['買主'] = c[0];
                }
                if (wjcCore.isString(item['売主'])) {
                    const c = this.customers.filter((x) => x['@rid'] == item['売主']);
                    if (c.length) item['売主'] = c[0];
                }
                if (wjcCore.isString(item['オークション'])) {
                    const a = this.auctions.filter((x) => x['@rid'] == item['オークション']);
                    if (a.length) item['オークション'] = a[0];
                }
            }
        }
        return items;
    }
}
